import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Notice } from '../ui/Notice';
import { api, ApiError } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { refreshQueries } from '../refresh';
import { FAILED_RESOURCE, formatWhen, NOTIFICATIONS_KEY, type FailedItem } from './types';

type Channels = { email: 'smtp' | 'log'; sms: 'smsapi' | 'log'; push: 'webpush' | 'off' };
type TestResult = { inApp: boolean; email: { mode: 'smtp' | 'log'; to: string | null; status: 'queued' | 'sent' | 'failed' | null; error: string | null } };

const CHANNEL_LABEL: Record<FailedItem['channel'], string> = { email: 'E-mail', sms: 'SMS', push: 'Push' };

function testMessage(r: TestResult): { ok: boolean; text: string } {
  const { email } = r;
  if (!email.to) return { ok: false, text: 'Powiadomienie jest w dzwonku. Twoje konto nie ma adresu e-mail, więc e-mail nie został wysłany.' };
  if (email.status === 'sent' && email.mode === 'smtp') return { ok: true, text: `Powiadomienie jest w dzwonku, a e-mail wysłano na ${email.to}. Sprawdź skrzynkę (także spam).` };
  if (email.status === 'sent') return { ok: true, text: `Powiadomienie jest w dzwonku. E-mail do ${email.to} zapisano tylko w dzienniku serwera, bo serwer poczty nie jest ustawiony (SMTP_URL).` };
  if (email.status === 'queued' && !email.error) return { ok: true, text: `Powiadomienie jest w dzwonku. E-mail do ${email.to}: w kolejce — wyjdzie za chwilę.` };
  return { ok: false, text: `Powiadomienie jest w dzwonku, ale e-maila do ${email.to} nie udało się wysłać: ${email.error ?? 'nieznany błąd'}. Aplikacja spróbuje ponownie za minutę.` };
}

/** Owner's screen: how messages leave the app, a test message to oneself, and the failed ones with "Ponów". */
export function FailedPage() {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const channels = useQuery({ queryKey: [...NOTIFICATIONS_KEY, 'channels'], queryFn: () => api<Channels>('/api/notifications/channels') });
  const failed = useQuery({ queryKey: [...NOTIFICATIONS_KEY, 'failed'], queryFn: () => api<{ messages: FailedItem[] }>('/api/notifications/failed') });
  const sendTest = useMutation({
    mutationFn: () => api<TestResult>('/api/notifications/test', { method: 'POST' }),
    onSettled: () => refreshQueries(queryClient, NOTIFICATIONS_KEY),
  });
  const retry = useMutation({
    mutationFn: (m: FailedItem) => api(`/api/notifications/failed/${m.id}/retry`, { method: 'POST' }),
    onSettled: () => refreshQueries(queryClient, NOTIFICATIONS_KEY),
  });
  if (!me) return null;
  const items = failed.data?.messages ?? [];
  const result = sendTest.data ? testMessage(sendTest.data) : null;
  const actionError = [sendTest.error, retry.error].find((e) => e instanceof ApiError) as ApiError | undefined;

  return (
    <section className="page" data-feature-id="notifications.failed">
      <div className="page-head">
        <h1>Wysyłka powiadomień</h1>
        {can(me, FAILED_RESOURCE, 'test') && (
          <button type="button" className="btn primary" disabled={sendTest.isPending} onClick={() => sendTest.mutate()}>
            {sendTest.isPending ? 'Wysyłanie…' : 'Wyślij testowe powiadomienie do siebie'}
          </button>
        )}
      </div>
      <p className="lede">Jak wychodzą e-maile i SMS-y z aplikacji oraz wiadomości, których nie udało się wysłać.</p>

      {result && !result.ok && (
        <p className="alert" role="status">
          {result.text}
        </p>
      )}
      {result?.ok && (
        <Notice className="ok" onClose={() => sendTest.reset()}>
          {result.text}
        </Notice>
      )}
      {actionError && (
        <p className="alert" role="alert">
          {actionError.status === 404 ? 'Ta wiadomość została już ponowiona. Lista została odświeżona.' : actionError.message}
        </p>
      )}

      {channels.data && (
        <dl className="channels">
          <div>
            <dt>E-mail</dt>
            <dd>{channels.data.email === 'smtp' ? 'wysyłany przez serwer poczty' : 'tylko dziennik serwera (nie ustawiono SMTP_URL)'}</dd>
          </div>
          <div>
            <dt>SMS</dt>
            <dd>{channels.data.sms === 'smsapi' ? 'wysyłany przez SMSAPI.pl' : 'tylko dziennik serwera (nie ustawiono SMSAPI_TOKEN)'}</dd>
          </div>
          <div>
            <dt>Push (telefon, komputer)</dt>
            <dd>{channels.data.push === 'webpush' ? 'włączony — każdy włącza go u siebie w „Moim koncie”' : 'wyłączony (nie ustawiono kluczy VAPID)'}</dd>
          </div>
        </dl>
      )}

      <h2 className="section-title">Nieudane powiadomienia</h2>
      {failed.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {failed.error && <p className="alert" role="alert">{failed.error instanceof ApiError ? failed.error.message : 'Nie udało się wczytać listy'}</p>}
      {failed.data && items.length === 0 && <p className="muted">Wszystkie powiadomienia zostały wysłane.</p>}
      {items.length > 0 && (
        <ul className="list">
          {items.map((m) => (
            <li key={m.id} className="row">
              <div className="row-main">
                <div className="row-title">
                  <strong>{m.subject}</strong>
                  <span className="chip">{CHANNEL_LABEL[m.channel]}</span>
                </div>
                <div className="muted">
                  {[m.recipientName, m.toAddress].filter(Boolean).join(' · ')} · {formatWhen(m.createdAt)} · prób: {m.attempts}
                </div>
                {m.lastError && <div className="failed-error">{m.lastError}</div>}
              </div>
              {can(me, FAILED_RESOURCE, 'retry') && (
                <div className="actions row-actions">
                  <button type="button" className="btn small" aria-label={`Ponów: ${m.subject}`} disabled={retry.isPending} onClick={() => retry.mutate(m)}>
                    Ponów
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
